import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Download, Share, SquarePlus, EllipsisVertical, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MTopBar } from "@/components/mobile/MTopBar";
import { MPage, Footer, cardCls, blueBtn } from "@/components/mobile/parts";
import { PushCard } from "@/components/mobile/PushCard";
import { useAuth } from "@/context/AuthContext";
import { isManagerRole } from "@/lib/api";
import { isIOS, isStandalone } from "@/lib/push";
import { canPrompt, onInstallChange, promptInstall } from "@/lib/install";
import { cn } from "@/lib/utils";

function StepHead({ n, title, done }) {
  return (
    <div className="flex items-center gap-3">
      <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-full font-display font-bold", done ? "bg-[#16a34a] text-white" : "bg-[#1565c0] text-white")}>{done ? <CheckCircle2 className="h-5 w-5" /> : n}</span>
      <p className="font-display text-lg font-semibold">{title}</p>
    </div>
  );
}

const Line = ({ icon: Icon, children }) => <li className="flex items-start gap-3 text-sm"><Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#1565c0]" /><span>{children}</span></li>;

function InstallStep() {
  const [, force] = useState(0);
  useEffect(() => onInstallChange(() => force((x) => x + 1)), []);
  const done = isStandalone() || localStorage.getItem("m_installed") === "1";
  const install = async () => {
    const r = await promptInstall();
    if (r === "accepted") toast.success("App instalado! Procure o ícone Comandas na tela inicial.");
  };
  return (
    <section className={cn(cardCls, "space-y-4 p-5")} data-testid="m-welcome-install">
      <StepHead n="1" title="Instale o app na tela inicial" done={done} />
      {done ? (
        <p className="text-sm text-emerald-700" data-testid="m-welcome-installed">{isStandalone() ? "Você já está usando o app instalado." : "App instalado. Daqui para frente, abra pelo ícone Comandas na tela inicial."}</p>
      ) : isIOS() ? (
        <ol className="space-y-3" data-testid="m-welcome-ios-steps">
          <Line icon={Smartphone}>Abra este link no <b>Safari</b>.</Line>
          <Line icon={Share}>Toque em <b>Compartilhar</b> (o quadrado com a seta para cima, na barra de baixo).</Line>
          <Line icon={SquarePlus}>Escolha <b>"Adicionar à Tela de Início"</b> e toque em <b>Adicionar</b>.</Line>
          <Line icon={CheckCircle2}>Abra pelo ícone <b>Comandas</b>, entre de novo e ative os avisos no passo 2.</Line>
        </ol>
      ) : canPrompt() ? (
        <Button onClick={install} className={cn("h-14 w-full rounded-xl text-base", blueBtn)} data-testid="m-welcome-install-btn"><Download className="mr-2 h-5 w-5" />Instalar app</Button>
      ) : (
        <ol className="space-y-3" data-testid="m-welcome-android-steps">
          <Line icon={EllipsisVertical}>Toque no menu <b>⋮</b> do Chrome, no canto de cima.</Line>
          <Line icon={Download}>Escolha <b>"Instalar app"</b> ou <b>"Adicionar à tela inicial"</b>.</Line>
          <Line icon={CheckCircle2}>Depois, abra sempre pelo ícone <b>Comandas</b>.</Line>
        </ol>
      )}
    </section>
  );
}

export default function MWelcome() {
  const { session } = useAuth();
  const nav = useNavigate();
  useEffect(() => { localStorage.setItem("m_onboarding_done", "1"); }, []);
  const manager = isManagerRole(session.user);
  return (
    <>
      <MTopBar title="Primeiro acesso" right={<button onClick={() => nav("/m", { replace: true })} className="px-2 text-sm font-semibold text-white/90" data-testid="m-welcome-skip">Pular</button>} />
      <MPage>
        <div className="fade-up">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="m-welcome-title">Bem-vindo(a), {session.user.name.split(" ")[0]}!</h1>
          <p className="mt-1 text-sm text-slate-500">Em dois passos o app de comandas da {session.tenant.nome_fantasia} fica pronto para o dia a dia.</p>
        </div>
        <InstallStep />
        <section className="space-y-3" data-testid="m-welcome-push">
          <div className="px-1"><StepHead n="2" title="Ative os avisos" /></div>
          <p className="px-1 text-sm text-slate-500">{manager ? "Para o celular tocar e vibrar quando um garçom pedir cancelamento ou desconto." : "Para saber na hora quando o gerente liberar ou recusar um pedido seu."}</p>
          <PushCard manager={manager} />
        </section>
        <p className="px-1 text-xs text-slate-400">Você pode rever estes passos em Configurações → "Instalar o app e ativar avisos".</p>
      </MPage>
      <Footer className="border-t bg-white p-4">
        <Button onClick={() => nav("/m", { replace: true })} className={cn("h-14 w-full rounded-xl text-lg", blueBtn)} data-testid="m-welcome-done-btn">Começar a usar o app</Button>
      </Footer>
    </>
  );
}
